/**
 * phase.ts — chassis-phase derived refs.
 *
 * Two phase vocabularies travel together inside the speedtest store:
 *
 *   - The engine's `Phase` (ping | download | upload | finished | null) —
 *     what the worker-wire format reports. `currentEnginePhase` strips the
 *     `finished` and `null` cases so consumers see a `PhaseId | undefined`.
 *   - The chassis's `ChassisPhase` (ready | ping | download | upload |
 *     complete) — what the instrument-chassis CSS reads through the
 *     `data-phase` attribute. The engine's null/finished cases collapse
 *     to `ready` and `complete` respectively.
 *
 * Both vocabularies stay distinct on purpose: the engine machine owns
 * the engine type, the chassis machine owns the 5-state shell. This file
 * is the one place the chassis-side vocabulary lives.
 *
 * `getSpeedtestStateAmount` and `getSpeedtestStateName` live here too
 * because the meter renderer reads them as paired with the chassis
 * phase — see `useMeterRenderer.ts` consumer-side wiring.
 */

import { computed, type ComputedRef, type Ref } from "vue";

import type { Phase } from "@speedtest/types.ts";
import { isPhaseId, type PhaseId } from "@src/types/speedtest.ts";

import type { LiveTestData, SpeedtestStateAmountKind } from "./types.ts";

/** Five-state vocabulary the chassis CSS reads via `data-phase`. */
export type ChassisPhase = "ready" | "ping" | "download" | "upload" | "complete";

const speedtestStateAmountKinds: ReadonlySet<string> = new Set([
    "Amount",
    "Progress",
]);

function isSpeedtestStateAmountKind(kind: string): kind is SpeedtestStateAmountKind {
    return speedtestStateAmountKinds.has(kind);
}

export interface PhaseMachine {
    /** Engine phase (PhaseId) when active; undefined when idle/finished. */
    currentEnginePhase: ComputedRef<PhaseId | undefined>;
    /** Chassis phase (5-state) — drives `data-phase` on the chassis shell. */
    currentPhase: ComputedRef<ChassisPhase>;
    /** Display label for the current chassis phase ("PING", "DOWNLOAD", etc.). */
    phaseLabel: ComputedRef<string>;
    /** Numeric value for the active phase. Drives the hero number; idle = 0. */
    heroValue: ComputedRef<number>;
    /** Display unit for the active phase. Defaults `Mbps`; ping reads `ms`. */
    unit: ComputedRef<string>;
    /** True when the chassis reads `ready`. */
    isIdle: ComputedRef<boolean>;
    /** Numeric amount lookup for the meter renderer. */
    getSpeedtestStateAmount: (phaseId: string, kind?: string) => number;
    /** Engine phase (or undefined when finished/null) for the meter renderer. */
    getSpeedtestStateName: () => Phase | undefined;
}

/** Construct the chassis-phase derived refs over a `LiveTestData | null`
 *  ref + a final-results-aware reader. The latter lets `heroValue`'s
 *  `complete` branch fall back to the post-test results snapshot. */
export function createPhaseMachine(
    data: Ref<LiveTestData | null>,
    finalUploadSpeed: () => number | undefined,
): PhaseMachine {
    const currentEnginePhase: ComputedRef<PhaseId | undefined> = computed(() => {
        const p = data.value?.phase;
        return p != null && isPhaseId(p) ? p : undefined;
    });

    const currentPhase: ComputedRef<ChassisPhase> = computed(() => {
        const p = data.value?.phase;
        if (p === "ping" || p === "download" || p === "upload") return p;
        if (p === "finished") return "complete";
        return "ready";
    });

    const phaseLabel: ComputedRef<string> = computed(() => {
        switch (currentPhase.value) {
            case "ping":     return "PING";
            case "download": return "DOWNLOAD";
            case "upload":   return "UPLOAD";
            case "complete": return "COMPLETE";
            default:          return "READY";
        }
    });

    const heroValue: ComputedRef<number> = computed(() => {
        switch (currentPhase.value) {
            case "ping":     return data.value?.ping ?? 0;
            case "download": return data.value?.downloadSpeed ?? 0;
            case "upload":   return data.value?.uploadSpeed ?? 0;
            case "complete": return finalUploadSpeed() ?? data.value?.uploadSpeed ?? 0;
            default:          return 0;
        }
    });

    const unit: ComputedRef<string> = computed(() => {
        if (currentPhase.value === "ping") return "ms";
        return "Mbps";
    });

    const isIdle: ComputedRef<boolean> = computed(
        () => currentPhase.value === "ready",
    );

    const stateAmounts: ComputedRef<
        Record<SpeedtestStateAmountKind, Record<PhaseId, number>>
    > = computed(() => {
        const currentData = data.value;
        return {
            Amount: {
                ping: currentData?.ping ?? 0,
                download: currentData?.downloadSpeed ?? 0,
                upload: currentData?.uploadSpeed ?? 0,
            },
            Progress: {
                ping: currentData?.pingProgress ?? 0,
                download: currentData?.downloadProgress ?? 0,
                upload: currentData?.uploadProgress ?? 0,
            },
        };
    });

    function getSpeedtestStateAmount(phaseId: string, kind: string = "Amount"): number {
        if (!isPhaseId(phaseId)) return 0;
        const lookupKind = isSpeedtestStateAmountKind(kind) ? kind : "Amount";
        return stateAmounts.value[lookupKind][phaseId];
    }

    function getSpeedtestStateName(): Phase | undefined {
        return data.value?.phase != null && data.value.phase !== "finished"
            ? data.value.phase
            : undefined;
    }

    return {
        currentEnginePhase,
        currentPhase,
        phaseLabel,
        heroValue,
        unit,
        isIdle,
        getSpeedtestStateAmount,
        getSpeedtestStateName,
    };
}
